import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, Building2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Logo } from "../../components/Logo";
import { LanguageToggle } from "../../components/LanguageToggle";
import { Button } from "../../components/ui/button";
import { send } from "../../components";
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from "../../hooks/useLoading";
import OrgRegNoModal from "./OrgRegNoModal";

const DanAuthPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { auth, setAuth } = useAuth()
    const { showLoading } = useLoading()
    const [loading, setLoading] = useState(false);
    const [loadingEntity, setLoadingEntity] = useState(false);
    const [showOrgModal, setShowOrgModal] = useState(false);
    const [danConfig, setDanConfig] = useState({
        authorizationUrl: "",
        clientId: "",
        redirectUri: window.location.origin + "/dan-callback",
        scope: "",
        scopeEntity: "",
        responseType: "code",
    });

    const state = { "action": "LOGIN", "uuid": "" + new Date().getTime() + (Math.random() + 1).toString(36).substring(7), "long": false }

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        const res = await send("io0016", {});

        if (res.status === 'error') {
            toast.error(res.message);
        } else {
            setDanConfig(prev => ({
                ...prev,
                authorizationUrl: res.url_authorize || prev.authorizationUrl,
                clientId: res.client_id || prev.clientId,
                redirectUri: res.redirect_uri || prev.redirectUri,
                scope: res.wsdls || prev.scope,
                scopeEntity: res.wsdlsEntity || prev.scopeEntity,
            }));
        }
    };

    const toBase64 = (obj) => {
        return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    };

    

    const handleDanEntityLogin = () => {
        if (!danConfig.clientId) {
            toast.error("DAN Configuration missing (Client ID)");
            return;
        }
        setShowOrgModal(true);
    };

    const handleOrgRegNoConfirm = (orgRegNo) => {
        setShowOrgModal(false);
        const entityState = { 
            ...state, 
            legalEntityNumber: orgRegNo
        };

        // Inject legalEntityNumber into scope based on the provided pattern
        let updatedScope = JSON.parse(JSON.stringify(danConfig.scopeEntity));
        if (Array.isArray(updatedScope)) {
            for (let i = 0; i < updatedScope.length; i++) {
                const element = updatedScope[i];
                if (element.services && element.services.length > 0
                    && element.services[0] === "WS100307_getLegalEntityInfoWithRegnum") {
                    
                    // Ensure the nested parameter structure exists
                    if (!element.params) element.params = {};
                    if (!element.params.WS100307_getLegalEntityInfoWithRegnum) {
                        element.params.WS100307_getLegalEntityInfoWithRegnum = {};
                    }
                    
                    element.params.WS100307_getLegalEntityInfoWithRegnum.legalEntityNumber = orgRegNo;
                }
            }
        }

        const url = danConfig.authorizationUrl +
            '?client_id=' + danConfig.clientId +
            '&scope=' + toBase64(updatedScope) +
            '&state=' + toBase64(entityState) +
            '&redirect_uri=' + danConfig.redirectUri +
            '&userid=' + auth.user.userid +
            '&response_type=code';

        setLoadingEntity(true);
        window.location.href = url;
    };


    const handleDanLogin = () => {
        if (!danConfig.clientId) {
            toast.error("DAN Configuration missing (Client ID)");
            return;
        }

        const url = danConfig.authorizationUrl +
            '?client_id=' + danConfig.clientId +
            '&scope=' + toBase64(danConfig.scope) +
            '&state=' + toBase64(state) +
            '&redirect_uri=' + danConfig.redirectUri +
            '&userid=' + auth.user.userid +
            '&response_type=code';

        setLoading(true);
        window.location.href = url;
    };

  

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const logout = async () => {
        setAuth({});
        await send("io0013", {}, showLoading)
        sessionStorage.clear()
        localStorage.clear()
        navigate('/');
    }

    return (
        <div className="min-h-screen flex flex-col">
            <header className="sticky top-0 flex items-center justify-between p-3 md:p-6 bg-white z-50">
                <Logo disabled={true} />
                <div className="flex items-center gap-3">
                    <LanguageToggle />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <LogOut className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">{t('logout')}</span>
                    </Button>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-lg space-y-8 animate-fade-up">
                    <div className="text-center">
                        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                            {t("danVerification") || "DAN Verification"}
                        </h2>
                        <p className="mt-2 text-muted-foreground">
                            {t("verifyWithDanSubtitle") || "Verify your identity using Digital Authentication Network"}
                        </p>
                    </div>

                    <div className="card-elevated p-8">
                        <div className="space-y-8">
                            <div className="bg-primary/5 border border-primary/10 rounded-2xl p-8 text-center space-y-6">
                                <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto shadow-sm border p-4">
                                    <img 
                                        src="/logos/negdi-logo.png" 
                                        alt="Negdi Logo" 
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="font-semibold text-xl">{t("officialVerification") || "Official Verification"}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {t("danDescription") || "We use DAN system for secure identity verification. This ensures your account is protected and verified."}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Button
                                        onClick={handleDanLogin}
                                        variant="gradient"
                                        size="xl"
                                        className="w-full h-auto py-3 text-base md:text-lg font-bold shadow-glow whitespace-normal break-words px-3"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                                                {t("verifying") || "Verifying..."}
                                            </>
                                        ) : (
                                            <>
                                                <ExternalLink className="mr-3 h-5 w-5 hidden md:block" />
                                                {t("verifyWithDan") || "Verify with DAN (Citizen)"}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="relative py-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-muted"></div>
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-card px-2 text-muted-foreground">
                                            {t("or") || "OR"}
                                        </span>
                                    </div>
                                </div>

                                <Button
                                    onClick={handleDanEntityLogin}
                                    variant="outline"
                                    size="lg"
                                    className="w-full h-auto py-3 border-2 hover:bg-primary/5 whitespace-normal break-words px-2"
                                    disabled={loadingEntity}
                                >
                                    {loadingEntity ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t("verifying") || "Verifying..."}
                                        </>
                                    ) : (
                                        <>
                                            <Building2 className="mr-2 h-4 w-4 hidden md:block" />
                                            {t("verifyWithDanEntity") || "Verify with DAN entity (Organization)"}
                                        </>
                                    )}
                                </Button>
                            </div>

                            <p className="text-center text-xs text-muted-foreground">
                                {t("danNote") || "You will be redirected to the official government login page."}
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <OrgRegNoModal
                open={showOrgModal}
                onOpenChange={setShowOrgModal}
                onConfirm={handleOrgRegNoConfirm}
                loading={loadingEntity}
            />
        </div>
    );
};

export default DanAuthPage;
